import {cpServiceGet} from 'api';
import {camelizeKeys} from 'utils/format-strings';
import {sendSentryException} from 'utils/error-handling';
import dayjs from 'utils/dayjs';
import {
    ReviewTagsResults,
    RawReviewTag,
    RawReviewTagsResponse,
    RawReviewTagsResults,
    ReviewTagRecord,
} from 'api/facilities/review-tags.types';
import {type Period} from 'pages/facilities/driver-feedback/review-tags/ReviewTags.types';

/**
 * Formats the request parameters for getting star ratings based on a selected period.
 * @group DTO Formatters
 */
export const formatRequest = (
    selectedPeriod: Period,
    reservationType: string[]
) => {
    const dateTo: string = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
    const dateFrom: string = dayjs()
        .subtract(
            parseInt(String(selectedPeriod.value), 10) - 1,
            selectedPeriod.unit as dayjs.ManipulateType
        )
        .format('YYYY-MM-DD');
    const reservationTypeString: string = reservationType
        .map((option) => option)
        .join(',');

    return {dateTo, dateFrom, reservationTypeString};
};

/**
 * Formats the response from the review tags API.
 * @group DTO Formatters
 */
export const formatResponse = (
    response: RawReviewTagsResponse
): ReviewTagsResults => {
    const formattedResponse: RawReviewTagsResults = camelizeKeys(response);

    return {
        ...formattedResponse,
        tags: formattedResponse.tags
            ? Object.entries(formattedResponse.tags)
                  .map(
                      ([label, data]: [
                          string,
                          RawReviewTag,
                      ]): ReviewTagRecord => ({
                          label,
                          ...data,
                      })
                  )
                  .sort((a, b) => b.count - a.count)
            : [],
    };
};

/**
 * Gets a facility's review tags within a date range.
 */
const getReviewTags = async (
    facilityId: number,
    selectedPeriod: Period,
    reservationType: string[]
): Promise<ReviewTagsResults> => {
    try {
        const {dateTo, dateFrom, reservationTypeString} = formatRequest(
            selectedPeriod,
            reservationType
        );
        const response = await cpServiceGet<RawReviewTagsResponse>(
            `facilities/${facilityId}/reviews/tags/?dateFrom=${dateFrom}&dateTo=${dateTo}${
                reservationTypeString
                    ? `&reservationTypes=${reservationTypeString}`
                    : ''
            }`
        );
        const formattedResponse = formatResponse(response.data.data);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);
        throw error;
    }
};

export default getReviewTags;
